import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, TrendingUp, Sparkles, Flame, Scale } from "lucide-react";
import { mumble, celebrateStreakMilestone } from "@/lib/quirks";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import EatingTimesCard from "@/components/insights/EatingTimesCard";
import AiReviewCard from "@/components/insights/AiReviewCard";
import PrimaryNav from "@/components/PrimaryNav";
import { useAuth } from "@/context/AuthContext";
import { kgToLb, kgToStone, round1 } from "@/lib/units";
import api from "@/lib/api";

// ---- pro-look primitives ----
const dowLabel = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }); }
  catch { return iso.slice(5); }
};

const avg = (arr) => {
  const v = arr.filter((x) => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

// Compact SVG area sparkline. Draws a subtle gradient fill + line stroke — feels
// closer to a Whoop/Strava dashboard tile than a hollow bar chart.
// Optional `goalValue` renders a dashed horizontal reference line at that
// value so athletes see "how far from goal" at a glance (used by Weight).
function Sparkline({ values, color, height = 60, goalValue = null, goalColor = "#F5F1E8" }) {
  const clean = values.map((v) => (v == null ? null : Number(v)));
  const nums = clean.filter((v) => v != null);
  if (nums.length < 2) {
    return <div style={{ height }} className="w-full flex items-center justify-center text-[10px] text-zinc-300">Not enough data yet</div>;
  }
  // Include the goal in min/max range so the goal line stays inside the plot
  // area — otherwise a goal well above/below the logged range clips off-screen.
  const withGoal = goalValue != null ? [...nums, goalValue] : nums;
  const min = Math.min(...withGoal), max = Math.max(...withGoal);
  const span = max - min || 1;
  const w = 240;
  const yFor = (v) => height - 6 - ((v - min) / span) * (height - 12);
  const pts = clean.map((v, i) => {
    const x = (i / (clean.length - 1)) * w;
    if (v == null) return null;
    return `${x},${yFor(v)}`;
  }).filter(Boolean);
  const linePath = "M " + pts.join(" L ");
  const areaPath = `${linePath} L ${w},${height} L 0,${height} Z`;
  const gid = `spark-${color.replace("#", "")}`;
  const goalY = goalValue != null ? yFor(goalValue) : null;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gid})`} />
      {goalY != null && (
        <>
          <line x1="0" y1={goalY} x2={w} y2={goalY} stroke={goalColor}
            strokeWidth="1.25" strokeDasharray="4 4" opacity="0.7" />
          <text x={w - 4} y={Math.max(10, goalY - 4)} textAnchor="end"
            fontSize="9" fontFamily="ui-monospace, SFMono-Regular, monospace"
            fill={goalColor} opacity="0.85">GOAL</text>
        </>
      )}
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {clean.map((v, i) => v == null ? null : (
        <circle key={i} cx={(i / (clean.length - 1)) * w} cy={yFor(v)} r="2" fill={color} />
      ))}
    </svg>
  );
}

function DeltaChip({ current, prev, invert = false, suffix = "" }) {
  if (current == null || prev == null || prev === 0) return null;
  const diff = current - prev;
  const pct = Math.abs(Math.round((diff / prev) * 100));
  const zero = Math.abs(diff) < 0.05;
  const good = zero ? null : (invert ? diff < 0 : diff > 0);
  const Icon = zero ? Minus : diff > 0 ? ArrowUpRight : ArrowDownRight;
  const color = zero ? "#9ca3af" : good ? "#34C759" : "#FF3B30";
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold px-1.5 py-0.5 border" style={{ color: "#F5F1E8", borderColor: color, background: "transparent" }}>
      <Icon className="h-3 w-3" /> {zero ? "0" : `${pct}%`}{suffix}
    </span>
  );
}

function StatTile({ title, icon: Icon, accent, value, unit, delta, series, hint, testid, goalValue = null, goalColor }) {
  return (
    <section data-testid={testid} className="border border-white/10 bg-gradient-to-b from-[#171717] to-[#0f0f0f] p-5 flex flex-col min-h-[190px]">
      <div className="flex items-center gap-2 mb-4">
        <span className="grid place-items-center h-8 w-8 shrink-0 border" style={{ borderColor: `${accent}55`, background: `${accent}12` }}>
          <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
        </span>
        <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 flex-1">{title}</p>
        {delta}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display leading-none text-5xl tracking-tight" style={{ color: value == null ? "#3f3f46" : accent }}>
          {value == null ? "—" : value}
        </span>
        {unit && value != null && <span className="text-[11px] uppercase tracking-widest text-zinc-300">{unit}</span>}
      </div>
      <div className="mt-4 flex-1">
        <Sparkline values={series} color={accent} goalValue={goalValue} goalColor={goalColor} />
      </div>
      {hint && <p className="text-[10px] text-zinc-300 mt-2">{hint}</p>}
    </section>
  );
}

export default function Insights() {
  const navigate = useNavigate();
  const { weightUnit, planGoalWeight } = useAuth();
  const [recap, setRecap] = useState([]);
  const [loading, setLoading] = useState(true);
  // 7d / 30d / 90d / 1y / 2y / lifetime. "lifetime" sends a very large days
  // number so the backend returns everything ever logged; the delta-vs-prev
  // metric is hidden in that mode since there's no prior window to compare to.
  const [range, setRange] = useState(7);
  const isLifetime = range === "lifetime";
  const rangeDays = isLifetime ? 9999 : range;
  const rangeLabel = ({ 7: "7 days", 30: "30 days", 90: "90 days",
                        365: "1 year", 730: "2 years", lifetime: "lifetime" })[range] || `${range} days`;
  const [trends, setTrends] = useState(null);
  const [eating, setEating] = useState(null);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const wellbeingDays = Math.max(Math.min(rangeDays, 730) * 2, 14);
        const [{ data }, tRes, eRes] = await Promise.all([
          api.get(`/wellbeing/recap?days=${wellbeingDays}`),
          api.get(`/insights/food-trends?days=${rangeDays}`).catch(() => ({ data: null })),
          api.get(`/insights/eating-times?days=${rangeDays}`).catch(() => ({ data: null })),
        ]);
        setRecap((data?.days || []).map((d) => ({ ...d, label: dowLabel(d.date) })));
        setTrends(tRes?.data || null);
        setEating(eRes?.data || null);
        // Streak-milestone confetti — fires once per athlete per milestone.
        const streak = data?.currentStreak || 0;
        const email = data?.email || "anon";
        if (streak) {
          try {
            const { toast } = await import("sonner");
            celebrateStreakMilestone(streak, email, (msg, opts) => toast.success(msg, opts));
          } catch { /* silent */ }
        }
      } catch (e) { console.error("Insights recap failed", e); }
      finally { setLoading(false); }
    })();
  }, [range, rangeDays]);

  const current = recap.slice(-range);
  const previous = recap.slice(-range * 2, -range);

  const kcalSeries = current.map((d) => d.kcal ?? null);
  // Weight sparkline — canonical kg from backend, converted to the athlete's
  // preferred unit (kg / lb / stone) for display so the graph reads natural.
  const kgToUnit = (kg) => {
    if (kg == null) return null;
    if (weightUnit === "lb") return round1(kgToLb(kg));
    if (weightUnit === "st") return +(kgToLb(kg) / 14).toFixed(2); // decimal stone for clean plotting
    return round1(kg);
  };
  const weightSeries = current.map((d) => kgToUnit(d.weightKg));
  const weightUnitLabel = weightUnit === "lb" ? "lb" : weightUnit === "st" ? "st" : "kg";
  // Latest logged weight in the window + previous window average for delta.
  const latestWeightKg = (() => {
    const withWeight = current.filter((d) => d.weightKg != null);
    return withWeight.length ? withWeight[withWeight.length - 1].weightKg : null;
  })();
  const firstWeightKg = (() => {
    const withWeight = current.filter((d) => d.weightKg != null);
    return withWeight.length ? withWeight[0].weightKg : null;
  })();
  const weightDeltaValue = latestWeightKg != null && firstWeightKg != null
    ? +(latestWeightKg - firstWeightKg).toFixed(1) : null;
  const weightDeltaDisplay = (() => {
    if (weightDeltaValue == null) return null;
    if (weightUnit === "lb") return round1(kgToLb(latestWeightKg) - kgToLb(firstWeightKg));
    if (weightUnit === "st") return +((kgToLb(latestWeightKg) - kgToLb(firstWeightKg)) / 14).toFixed(2);
    return weightDeltaValue;
  })();
  const weightLatestDisplay = (() => {
    if (latestWeightKg == null) return null;
    if (weightUnit === "st") { const s = kgToStone(latestWeightKg); return `${s.st}·${s.lb}`; }
    return kgToUnit(latestWeightKg);
  })();
  // Goal-weight overlay for the sparkline — converted into the same unit as
  // the plotted series so the dashed reference line lines up numerically.
  const weightGoalPlot = planGoalWeight != null ? kgToUnit(planGoalWeight) : null;
  const weightGoalDisplay = (() => {
    if (planGoalWeight == null) return null;
    if (weightUnit === "lb") return `${round1(kgToLb(planGoalWeight))} lb`;
    if (weightUnit === "st") { const s = kgToStone(planGoalWeight); return `${s.st}st ${s.lb}lb`; }
    return `${round1(planGoalWeight)} kg`;
  })();
  const weightToGoalDelta = (() => {
    if (planGoalWeight == null || latestWeightKg == null) return null;
    const diffKg = latestWeightKg - planGoalWeight;
    if (Math.abs(diffKg) < 0.05) return { sign: "", value: 0, unit: weightUnitLabel, atGoal: true };
    if (weightUnit === "lb") {
      const d = round1(kgToLb(latestWeightKg) - kgToLb(planGoalWeight));
      return { sign: d > 0 ? "+" : "", value: d, unit: "lb", atGoal: false };
    }
    if (weightUnit === "st") {
      const totalLb = kgToLb(latestWeightKg) - kgToLb(planGoalWeight);
      const sign = totalLb >= 0 ? "+" : "-";
      const abs = Math.abs(totalLb);
      const st = Math.floor(abs / 14);
      const lb = Math.round(abs - st * 14);
      return { sign, value: `${st}st ${lb}lb`, unit: "", atGoal: false };
    }
    return { sign: diffKg > 0 ? "+" : "", value: +diffKg.toFixed(1), unit: "kg", atGoal: false };
  })();

  const kcalAvg = avg(kcalSeries); const kcalAvgPrev = avg(previous.map((d) => d.kcal));
  const daysLogged = current.filter((d) => (d.kcal || 0) > 0).length;

  const fmt = (v, digits = 0) => v == null ? null : (digits === 0 ? Math.round(v) : Number(v.toFixed(digits)));

  // One-line coaching signal, computed against the previous 7 days.
  const insight = useMemo(() => {
    if (kcalAvg != null && kcalAvgPrev != null && Math.abs(kcalAvg - kcalAvgPrev) >= 150) {
      return kcalAvg > kcalAvgPrev
        ? `Eating ${Math.round(kcalAvg - kcalAvgPrev)} kcal/day more than last week — check it matches your training load.`
        : `Eating ${Math.round(kcalAvgPrev - kcalAvg)} kcal/day less than last week — watch for under-fuelling on hard days.`;
    }
    if (daysLogged >= 6) return `${daysLogged} of ${range} days logged — that consistency is what makes the numbers trustworthy.`;
    if (daysLogged > 0) return `${daysLogged} days logged so far — log a few more and we'll spot the patterns for you.`;
    return "Keep logging — a few more days and we'll spot the patterns for you.";
  }, [kcalAvg, kcalAvgPrev, daysLogged, range]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav />
      </header>

      <main data-testid="insights-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#E2E30A]">Reflect</p>
            <h1 className="font-display text-4xl sm:text-6xl uppercase tracking-tight mt-2 leading-none">
              {isLifetime ? "Your lifetime story" : `Your last ${rangeLabel}`}
            </h1>
          </div>
          <div className="inline-flex flex-wrap items-center gap-1 border border-white/10 bg-[#0A0A0A] p-1" data-testid="insights-range">
            {[
              { v: 7, label: "7d" },
              { v: 30, label: "30d" },
              { v: 90, label: "90d" },
              { v: 365, label: "1yr" },
              { v: 730, label: "2yr" },
              { v: "lifetime", label: "All" },
            ].map(({ v, label }) => (
              <button key={v} type="button" data-testid={`insights-range-${v}`} onClick={() => setRange(v)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors ${range === v ? "bg-[#E2E30A] text-black" : "text-zinc-300 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Coach insight banner */}
        <div data-testid="insights-summary" className="relative border border-white/10 bg-[#141414] px-4 sm:px-5 py-4">
          <div className="flex items-center gap-4">
            <span className="grid place-items-center h-12 w-12 shrink-0 bg-[#E2E30A]" style={{ boxShadow: "0 8px 24px -14px #E2E30A" }}>
              <Sparkles className="h-6 w-6 text-black" strokeWidth={2.2} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white leading-tight break-words">Coach's read</span>
              <span className="block text-xs text-zinc-300 mt-1 leading-snug break-words">One-line signal from your last {rangeLabel}</span>
            </span>
          </div>
          <p className="text-base sm:text-lg text-zinc-100 mt-3 leading-snug">{insight}</p>
        </div>

        {/* ---- DAILY SNAPSHOT — hero group of everyday-life metrics
             (calories logged + weight). Front-loaded so the athlete sees "how am I doing
             day to day" first, before the training/nutrition breakdown. */}
        {!loading && (
          <>
            <div className="flex items-center gap-3 pt-2" data-testid="insights-section-daily">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-semibold">01 · Daily Snapshot</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <StatTile testid="insights-kcal" title="Calories logged" icon={Flame} accent="#E2E30A"
                value={fmt(kcalAvg)} unit="kcal / day"
                delta={<DeltaChip current={kcalAvg} prev={kcalAvgPrev} />}
                series={kcalSeries}
                hint={`${daysLogged} of the last ${range} days logged. Daily average of everything you ticked or added.`} />
              {weightSeries.some((v) => v != null) ? (
                <StatTile testid="insights-weight" title={`Weight · ${weightUnitLabel}`} icon={Scale} accent="#4C9AFF"
                  value={weightLatestDisplay}
                  unit={weightUnit === "st" ? "st·lb" : weightUnitLabel}
                  delta={weightDeltaDisplay != null && weightDeltaDisplay !== 0 ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold px-1.5 py-0.5 border"
                      style={{ color: "#F5F1E8", borderColor: weightDeltaDisplay > 0 ? "#FF9500" : "#34C759", background: "transparent" }}>
                      {weightDeltaDisplay > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {weightDeltaDisplay > 0 ? "+" : ""}{weightDeltaDisplay}{weightUnitLabel}
                    </span>
                  ) : null}
                  series={weightSeries}
                  goalValue={weightGoalPlot}
                  goalColor="#E2E30A"
                  hint={weightGoalDisplay
                    ? weightToGoalDelta?.atGoal
                      ? `At goal · ${weightGoalDisplay}. Weigh in the morning for a clean trend.`
                      : `Goal ${weightGoalDisplay} · you are ${weightToGoalDelta.sign}${weightToGoalDelta.value}${weightToGoalDelta.unit ? " " + weightToGoalDelta.unit : ""} from it.`
                    : `Latest logged weigh-in over the last ${rangeLabel}. Weigh in the morning for a clean trend.`} />
              ) : (
                <section className="border border-dashed border-white/15 p-5 flex flex-col justify-center min-h-[190px]" data-testid="insights-weight-empty">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="grid place-items-center h-8 w-8 border border-[#4C9AFF]/40 bg-[#4C9AFF]/10">
                      <Scale className="h-3.5 w-3.5 text-[#4C9AFF]" />
                    </span>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Weight · {weightUnitLabel}</p>
                  </div>
                  <p className="text-sm text-zinc-400">Log your weight on the Tracker for a few days to unlock the trend line.</p>
                </section>
              )}
            </div>

            <div className="flex items-center gap-3 pt-4" data-testid="insights-section-training">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-semibold">02 · Training</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          </>
        )}

        {!loading && (() => {
          // Session Consistency — a coach's dream metric. Count planned vs
          // actually-ticked sessions in the current window; cap displayed % at
          // 999 in case a rest day gets a bonus completed session.
          const planned = current.reduce((a, d) => a + (d.plannedSessions || 0), 0);
          const completed = current.reduce((a, d) => a + Math.min(d.completedSessions || 0, d.plannedSessions || 0), 0);
          const extras = current.reduce((a, d) => a + Math.max(0, (d.completedSessions || 0) - (d.plannedSessions || 0)), 0);
          const pct = planned > 0 ? Math.round((completed / planned) * 100) : null;
          const color = pct == null ? "#3f3f46" : pct >= 90 ? "#34C759" : pct >= 65 ? "#E2E30A" : "#FF9500";
          const C = 2 * Math.PI * 46; // circumference
          const dash = pct == null ? 0 : (Math.min(pct, 100) / 100) * C;
          return (
            <section data-testid="insights-consistency" className="border border-white/10 bg-gradient-to-b from-[#171717] to-[#0f0f0f] p-5 sm:p-6">
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                <div className="relative shrink-0" style={{ width: 120, height: 120 }}>
                  <svg viewBox="0 0 120 120" width="120" height="120">
                    <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                    {pct != null && (
                      <circle cx="60" cy="60" r="46" fill="none" stroke={color} strokeWidth="10"
                        strokeDasharray={`${dash} ${C}`} strokeDashoffset="0" strokeLinecap="round"
                        transform="rotate(-90 60 60)" style={{ transition: "stroke-dasharray 400ms" }} />
                    )}
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                      <p className="font-display leading-none text-3xl" style={{ color }}>{pct == null ? "—" : `${pct}%`}</p>
                      <p className="text-[9px] uppercase tracking-widest text-zinc-300 mt-1">done</p>
                    </div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Session Consistency · Last {range} days</p>
                  <p className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-2 leading-tight">
                    <span className="text-[#E2E30A]">{completed}</span>
                    <span className="text-zinc-300"> / {planned}</span>
                    <span className="text-zinc-400 text-base"> sessions ticked</span>
                  </p>
                  {extras > 0 && <p className="text-[11px] text-[#34C759] mt-1">+{extras} bonus session{extras > 1 ? "s" : ""} logged beyond plan</p>}
                  <p className="text-[11px] text-zinc-300 mt-2">
                    {planned === 0 ? "No planned sessions in your plan for this range yet."
                      : pct >= 90 ? "Nailing it. Coach would be proud."
                      : pct >= 65 ? "Solid — a couple of missed sessions is normal."
                      : "Consistency is slipping. One easy session this week beats zero perfect ones."}
                  </p>
                </div>
              </div>
            </section>
          );
        })()}

        {/* ---- MILESTONES — celebration cards. Displayed after training so
             the athlete sees their wins before the numbers get granular. */}

        {!loading && current.length > 0 && (() => {
          // Logging streaks and best days, judged on nutrition only.
          let cur = 0, longest = 0;
          [...recap].reverse().forEach((d) => {
            const logged = (d.kcal || 0) > 0 || d.notes;
            if (logged) { cur += 1; longest = Math.max(longest, cur); } else cur = 0;
          });
          const onTarget = current.filter((d) => d.target && d.kcal
            && Math.abs(d.kcal - d.target) / d.target <= 0.1).length;
          const bestProteinDay = current
            .filter((d) => d.protein != null)
            .reduce((a, b) => (a && a.protein >= b.protein ? a : b), null);
          if (longest === 0 && !bestProteinDay) return null;
          return (
            <>
              <div className="flex items-center gap-3 pt-4" data-testid="insights-section-milestones">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-semibold">03 · Milestones</p>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <section className="border border-white/10 bg-[#141414] p-5" data-testid="insights-personal-bests">
                <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Logging &amp; targets</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="font-display text-3xl text-[#FF9500] leading-none">{longest}</p>
                    <p className="text-[9px] uppercase tracking-wider text-zinc-300 mt-2">Longest log streak</p>
                  </div>
                  <div className="text-center">
                    <p className="font-display text-3xl text-[#34C759] leading-none">{onTarget}</p>
                    <p className="text-[9px] uppercase tracking-wider text-zinc-300 mt-2">Days within 10% of target</p>
                  </div>
                  <div className="text-center">
                    <p className="font-display text-3xl text-[#4C9AFF] leading-none">{bestProteinDay?.protein ? Math.round(bestProteinDay.protein) : "—"}</p>
                    <p className="text-[9px] uppercase tracking-wider text-zinc-300 mt-2">Best protein day (g)</p>
                  </div>
                </div>
              </section>
            </>
          );
        })()}

        {/* Weight + Food-trend cards. All optional — degrade gracefully when
            there's no data yet (new users). */}
        {/* ---- NUTRITION — all food-classification trends grouped together. */}
        {!loading && trends && (
          <div className="flex items-center gap-3 pt-4" data-testid="insights-section-nutrition">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-semibold">04 · Nutrition</p>
            <div className="flex-1 h-px bg-white/10" />
          </div>
        )}

        {!loading && trends && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="insights-food-trends">
            {/* 5-a-day */}
            <section className="border border-white/10 bg-[#141414] p-5" data-testid="insights-fiveaday-card">
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Fruit &amp; Veg · 5-a-day</p>
              {(() => {
                const v = trends.foodTrends.fiveADay;
                const pct = Math.min(100, Math.round((v.avgPerDay / 5) * 100));
                const col = v.avgPerDay >= 5 ? "#34C759" : v.avgPerDay >= 3 ? "#E2E30A" : "#FF9500";
                return (
                  <>
                    <p className="font-display text-4xl leading-none" style={{ color: col }}>{v.avgPerDay}<span className="text-sm text-zinc-400 ml-1">/ 5 per day</span></p>
                    <div className="h-2 mt-3 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col }} />
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-2">
                      {v.totalWindow} portions this week · last week averaged {v.lastWeekAvgPerDay}/day
                    </p>
                  </>
                );
              })()}
            </section>

            {/* Protein mix */}
            <section className="border border-white/10 bg-[#141414] p-5" data-testid="insights-protein-mix-card">
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Protein sources · last {range} days</p>
              {trends.foodTrends.proteinMix.totalG > 0 ? (
                <>
                  <p className="text-sm text-zinc-300 mb-3"><b className="text-white">{trends.foodTrends.proteinMix.totalG}g</b> total protein logged</p>
                  <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
                    {[
                      { k: "meat", label: "Meat", color: "#FF6B57" },
                      { k: "fish", label: "Fish", color: "#4C9AFF" },
                      { k: "dairy", label: "Dairy / eggs", color: "#E2E30A" },
                      { k: "plant", label: "Plant", color: "#34C759" },
                    ].map((s) => (
                      <div key={s.k} title={`${s.label}: ${trends.foodTrends.proteinMix.pct[s.k]}%`}
                        style={{ width: `${trends.foodTrends.proteinMix.pct[s.k]}%`, background: s.color }} />
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 text-[11px]">
                    {[
                      { k: "meat", label: "Meat", color: "#FF6B57" },
                      { k: "fish", label: "Fish", color: "#4C9AFF" },
                      { k: "dairy", label: "Dairy", color: "#E2E30A" },
                      { k: "plant", label: "Plant", color: "#34C759" },
                    ].map((s) => (
                      <div key={s.k}>
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: s.color }} />
                        <span className="text-zinc-300">{s.label}</span>
                        <p className="text-white font-mono ml-3.5">{trends.foodTrends.proteinMix.pct[s.k]}%</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-zinc-400">Log a few meals to see your protein source balance.</p>
              )}
            </section>

            {/* Processed food ratio */}
            <section className="border border-white/10 bg-[#141414] p-5" data-testid="insights-processed-card">
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Processed food ratio</p>
              {trends.foodTrends.processed.total > 0 ? (() => {
                const v = trends.foodTrends.processed;
                const col = v.pct <= 15 ? "#34C759" : v.pct <= 30 ? "#E2E30A" : "#FF6B57";
                const delta = v.pct - v.lastWeekPct;
                return (
                  <>
                    <p className="font-display text-4xl leading-none" style={{ color: col }}>{v.pct}%</p>
                    <p className="text-[11px] text-zinc-400 mt-1">{v.count} of {v.total} items · last week {v.lastWeekPct}%</p>
                    <p className={`text-[11px] mt-2 ${delta > 0 ? "text-[#FF9500]" : delta < 0 ? "text-[#34C759]" : "text-zinc-400"}`}>
                      {delta === 0 ? "Steady vs last week" : delta > 0 ? `▲ ${delta} pts more processed than last week` : `▼ ${-delta} pts less processed than last week`}
                    </p>
                  </>
                );
              })() : (
                <p className="text-sm text-zinc-400">No logs yet — this appears once you've tracked a few days.</p>
              )}
            </section>

            {/* Meat vs plant */}
            <section className="border border-white/10 bg-[#141414] p-5 md:col-span-2" data-testid="insights-meat-plant-card">
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Meat vs plant portions · last {range} days</p>
              {(() => {
                const m = trends.foodTrends.meatVsPlant;
                const total = m.meatPortions + m.plantPortions;
                if (!total) return <p className="text-sm text-zinc-400">Log meals for a few days to see your meat vs plant balance.</p>;
                const meatPct = Math.round((m.meatPortions / total) * 100);
                const plantPct = 100 - meatPct;
                return (
                  <>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#FF6B57] font-mono"><b>{m.meatPortions}</b> meat portions ({meatPct}%)</span>
                      <span className="text-[#34C759] font-mono">({plantPct}%) <b>{m.plantPortions}</b> plant portions</span>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
                      <div style={{ width: `${meatPct}%`, background: "#FF6B57" }} />
                      <div style={{ width: `${plantPct}%`, background: "#34C759" }} />
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-2">
                      Last week: {m.lastWeek.meat} meat · {m.lastWeek.plant} plant
                    </p>
                  </>
                );
              })()}
            </section>
          </div>
        )}

        {/* ---- EATING TIMES — when the day's energy actually lands. Only
             uses entries logged with a time stamp (June 2026 onwards). */}
        {!loading && eating && eating.coverage.timed > 0 && (          <>
            <div className="flex items-center gap-3 pt-4" data-testid="insights-section-eating-times">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#4C9AFF] font-semibold">05 · Eating times</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <EatingTimesCard eating={eating} rangeLabel={rangeLabel} />
          </>
        )}
        {!loading && eating && eating.coverage.timed === 0 && (
          <section className="border border-dashed border-white/15 p-5" data-testid="insights-eating-times-empty">
            <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-2">Eating times</p>
            <p className="text-sm text-zinc-300">Log some food in the Tracker and we'll chart when your energy lands across the day — first meal, last meal, eating window and post-session refuel.</p>
          </section>
        )}

        {/* ---- AI REVIEW — plain-English read on the week + 3 actions. */}
        {!loading && (
          <>
            <div className="flex items-center gap-3 pt-4" data-testid="insights-section-ai">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#A78BFA] font-semibold">06 · AI review</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <AiReviewCard days={rangeDays} rangeLabel={rangeLabel} />
          </>
        )}

        {/* ---- DAY BY DAY — the drill-down at the very end, for athletes
             who want to inspect individual days after seeing the summary. */}
        {!loading && (
          <div className="flex items-center gap-3 pt-4" data-testid="insights-section-recap">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2E30A] font-semibold">07 · This week, day by day</p>
            <div className="flex-1 h-px bg-white/10" />
          </div>
        )}
      </main>
    </div>
  );
}
